import { useCallback, useEffect, useMemo } from "react"

import { type PreviewReportDatasourceResult, type ReportTemplateDatasourceDto, type ReportTemplateFlowSectionDto, type ReportLayoutWidgetDto } from "../../api/reportTemplateService"
import {
  TABLE_ROW_NO_KEY,
  WORKER_TABLE_COLUMNS,
  clamp,
  estimateFieldRenderedHeight,
  parseTableColumnWidthMap,
  resolveWidgetLabel,
  type TableColumnWidthMap,
  type TableSourceMode,
} from "./editorUtils"
import { buildPreviewBodyPageEntries } from "./previewLayoutRuntime"
import { getDatasourceFieldDisplay, getDatasourceFieldLabel, getDatasourceTableRows } from "./reportDatasourceRuntime"

const formatPreviewValue = (value: unknown): string => {
  if (value == null) return "-"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "object") return JSON.stringify(value)
  const text = String(value)
  return text.trim() ? text : "-"
}

interface UseLayoutPreviewCompositionParams {
  datasources: ReportTemplateDatasourceDto[]
  datasourcePreviewResults: Record<string, PreviewReportDatasourceResult>
  headerWidgets: ReportLayoutWidgetDto[]
  bodyWidgets: ReportLayoutWidgetDto[]
  footerWidgets: ReportLayoutWidgetDto[]
  baseBodyCanvasHeight: number
  flowSections: ReportTemplateFlowSectionDto[]
  manualPageCount: number
  primaryFlowSectionId: string
  previewRows: Array<Record<string, unknown>>
  previewWorkers: unknown[]
  fieldLabelMap: Map<string, string>
  previewValueMap: Map<string, string>
  previewPageIndex: number
  setPreviewPageIndex: (updater: (prev: number) => number) => void
}

export const useLayoutPreviewComposition = ({
  datasources,
  datasourcePreviewResults,
  headerWidgets,
  bodyWidgets,
  footerWidgets,
  baseBodyCanvasHeight,
  flowSections,
  manualPageCount,
  primaryFlowSectionId,
  previewRows,
  previewWorkers,
  fieldLabelMap,
  previewValueMap,
  previewPageIndex,
  setPreviewPageIndex,
}: UseLayoutPreviewCompositionParams) => {
  const getFieldDisplay = useCallback(
    (fieldKey: string, datasourceId?: string) => {
      if (datasourceId) {
        return getDatasourceFieldDisplay(datasourcePreviewResults, datasourceId, fieldKey)
      }
      const value = previewValueMap.get(fieldKey)
      if (value && value.trim()) return value
      return `[${fieldKey || "-"}]`
    },
    [datasourcePreviewResults, previewValueMap],
  )

  const getTablePreviewRows = useCallback(
    (widget: ReportLayoutWidgetDto, columns: string[]) => {
      if (columns.length === 0) return []
      const source: TableSourceMode =
        String(widget.props?.tableSource || "reportRows") === "workers"
          ? "workers"
          : String(widget.props?.tableSource || "reportRows") === "datasource"
            ? "datasource"
            : "reportRows"

      if (source === "workers") {
        return previewWorkers.slice(0, 20).map((worker, rowIndex) =>
          columns.map((columnKey) => {
            if (columnKey === TABLE_ROW_NO_KEY) return String(rowIndex + 1)
            const value = (worker as unknown as Record<string, unknown>)[columnKey]
            return formatPreviewValue(value)
          }),
        )
      }

      if (source === "datasource") {
        return getDatasourceTableRows(
          datasourcePreviewResults,
          String(widget.props?.datasourceId || ""),
          columns.filter((columnKey) => columnKey !== TABLE_ROW_NO_KEY),
        ).map((row, rowIndex) =>
          columns.map((columnKey, columnIndex) => {
            if (columnKey === TABLE_ROW_NO_KEY) return String(rowIndex + 1)
            const safeIndex = columns.slice(0, columnIndex).filter((item) => item !== TABLE_ROW_NO_KEY).length
            return row[safeIndex] ?? "-"
          }),
        )
      }

      if (previewRows.length === 0) return []
      return previewRows.slice(0, 20).map((row, rowIndex) =>
        columns.map((columnKey) => {
          if (columnKey === TABLE_ROW_NO_KEY) return String(rowIndex + 1)
          return formatPreviewValue(row[columnKey])
        }),
      )
    },
    [datasourcePreviewResults, previewRows, previewWorkers],
  )

  const getTableColumnLabel = useCallback(
    (widget: ReportLayoutWidgetDto, columnKey: string) => {
      if (columnKey === TABLE_ROW_NO_KEY) return "No."
      const source: TableSourceMode =
        String(widget.props?.tableSource || "reportRows") === "workers"
          ? "workers"
          : String(widget.props?.tableSource || "reportRows") === "datasource"
            ? "datasource"
            : "reportRows"
      if (source === "workers") {
        return WORKER_TABLE_COLUMNS.find((item) => item.key === columnKey)?.label || columnKey
      }
      if (source === "datasource") {
        return getDatasourceFieldLabel(
          datasources,
          datasourcePreviewResults,
          String(widget.props?.datasourceId || ""),
          columnKey,
        )
      }
      return fieldLabelMap.get(columnKey) || columnKey
    },
    [datasourcePreviewResults, datasources, fieldLabelMap],
  )

  const getTableColumnWidths = useCallback((widget: ReportLayoutWidgetDto, columns: string[]): TableColumnWidthMap => {
    const map = parseTableColumnWidthMap(widget.props?.columnWidths)
    if (columns.length === 0) return {}
    const next: TableColumnWidthMap = {}
    columns.forEach((columnKey) => {
      const width = map[columnKey]
      if (Number.isFinite(width) && width > 0) {
        next[columnKey] = width
      }
    })
    return next
  }, [])

  const previewHeaderWidgets = useMemo(
    () => [...(headerWidgets ?? [])].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)),
    [headerWidgets],
  )
  const previewBodyWidgets = useMemo(
    () => [...(bodyWidgets ?? [])].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)),
    [bodyWidgets],
  )
  const previewFooterWidgets = useMemo(
    () => [...(footerWidgets ?? [])].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)),
    [footerWidgets],
  )

  const previewBodyPageEntries = useMemo(
    () =>
      buildPreviewBodyPageEntries({
        baseBodyCanvasHeight,
        flowSections,
        manualPageCount,
        previewBodyWidgets,
        primaryFlowSectionId,
        getTablePreviewRows,
        getWidgetMeasuredHeight: (widget) => {
          if (widget.type !== "field") return undefined
          const fieldKey = String(widget.props?.fieldKey || "")
          const fieldLabel = resolveWidgetLabel(widget, fieldLabelMap.get(fieldKey) || "Field")
          const fieldValue = getFieldDisplay(fieldKey, String(widget.props?.datasourceId || ""))
          return estimateFieldRenderedHeight(widget, fieldLabel, fieldValue)
        },
      }),
    [baseBodyCanvasHeight, fieldLabelMap, flowSections, getFieldDisplay, getTablePreviewRows, manualPageCount, previewBodyWidgets, primaryFlowSectionId],
  )

  const previewTotalPages = useMemo(() => {
    const indices = Array.from(previewBodyPageEntries.keys())
    if (indices.length === 0) return Math.max(1, manualPageCount)
    return Math.max(1, manualPageCount, Math.max(...indices) + 1)
  }, [manualPageCount, previewBodyPageEntries])

  useEffect(() => {
    setPreviewPageIndex((prev) => clamp(prev, 0, Math.max(0, previewTotalPages - 1)))
  }, [previewTotalPages, setPreviewPageIndex])

  const previewBodyRenderEntries = useMemo(
    () => previewBodyPageEntries.get(previewPageIndex) ?? [],
    [previewBodyPageEntries, previewPageIndex],
  )

  return {
    getFieldDisplay,
    getTableColumnLabel,
    getTableColumnWidths,
    previewHeaderWidgets,
    previewFooterWidgets,
    previewBodyRenderEntries,
    previewTotalPages,
  }
}
